import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * ContactModal — a small guestbook-style form.
 * On submit: POST /api/messages (public endpoint, saves to Mongo).
 * Always also offers a plain mailto: fallback so nothing is a dead end.
 */
export default function ContactModal({ open, onClose, project = '', title = 'Drop me a note' }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Prevent background scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post('/messages', {
        name: name.trim(),
        email: email.trim(),
        body: body.trim(),
        project: project || 'general',
        subject: project ? `Interested in ${project}` : '',
      });
      toast.success('Sent. I read every one.');
      setName(''); setEmail(''); setBody('');
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong.';
      toast.error(typeof detail === 'string' ? detail : 'Please check your entries.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="contact-modal"
          className="fixed inset-0 z-[100] bg-void/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full max-w-xl bg-paper border border-ink/20 p-6 md:p-10 my-auto"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">A door · not a plea</div>
              <button onClick={onClose} data-testid="contact-modal-close" aria-label="Close" className="mono text-sm text-ink/60 hover:text-ink">✕</button>
            </div>

            <h3 className="serif text-3xl md:text-4xl text-ink leading-tight mb-2">{title}</h3>
            <p className="mono text-xs text-ink/60 mb-6">
              Saved straight to my private message board (I check it daily). Or, if you prefer, email me directly at{' '}
              <a href={`mailto:eileen.tadeo@gmail.com${project ? `?subject=Interested%20in%20${encodeURIComponent(project)}` : ''}`} className="underline decoration-orchid decoration-2 underline-offset-4 hover:text-orchid">
                eileen.tadeo@gmail.com
              </a>.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Name</label>
                <input
                  data-testid="contact-name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  required minLength={1} maxLength={120}
                  className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-base py-2"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Email</label>
                <input
                  data-testid="contact-email"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-base py-2"
                />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Note</label>
                <textarea
                  data-testid="contact-body"
                  value={body} onChange={(e) => setBody(e.target.value)}
                  required minLength={5} maxLength={5000} rows={5}
                  placeholder={project ? `What caught your eye about ${project}?` : 'What are you working on?'}
                  className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-3 leading-relaxed resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={onClose} className="mono text-[11px] uppercase tracking-[0.22em] px-5 py-3 border border-ink/20 hover:border-orchid">Cancel</button>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="contact-submit"
                  className="bg-void text-paper px-6 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors disabled:opacity-60"
                >
                  {busy ? 'Sending…' : 'Send →'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
